import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Smartphone, Eye, EyeOff, LogIn, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { authLogin, authMe } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { signIn } = useApp();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mobile.length !== 10) {
      setError("Mobile must be exactly 10 digits.");
      return;
    }
    if (!password) {
      setError("Password is required.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await authLogin(mobile, password);
      await signIn(data.access_token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      // Land on the first module the user actually has access to (dashboard by default).
      let landing = "/";
      if (!data.must_change_password) {
        try {
          const me = await authMe();
          const p = me.module_permissions || {};
          if (me.role !== "admin") {
            if (!p.dashboard?.view) {
              if (p.spares?.view) landing = "/workbench";
              else if (p.reports?.view) landing = "/orders";
              else if (p.av?.view) landing = "/av/dashboard";
              else if (p.pv?.view) landing = "/pv/dashboard";
              else if (p.masters?.view) landing = "/masters/pv-status";
              else if (p.security?.view) landing = "/security/scan";
            }
          }
        } catch { /* fall back to "/" */ }
      } else {
        landing = "/account/password";
      }
      navigate(landing, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003399] via-[#0f1d6b] to-[#1a1b22] p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2.5 text-white font-bold text-2xl tracking-tight">
            <img
              src="/logo.png"
              alt="Service Sync"
              className="w-10 h-10 rounded-lg ring-2 ring-white/20 shadow-lg"
              data-testid="login-logo"
            />
            IVA Motors
          </div>
          <div className="text-[12px] uppercase tracking-[0.3em] text-white/60 mt-0.5">
            Service Sync
          </div>
        </div>

        <div className="bg-white border border-[#e2e1eb] rounded-2xl shadow-xl p-7">
          <h1 className="text-[20px] font-bold text-[#1a1b22]">Sign in</h1>
          <p className="text-[12.5px] text-[#747684] mt-1 mb-5">
            Use your registered mobile number and password.
          </p>

          {error && (
            <div
              data-testid="login-error"
              className="mb-3 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-[12px] rounded-lg p-2.5"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3.5">
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684]">
                Mobile Number
              </label>
              <div className="mt-1 relative">
                <Smartphone className="w-4 h-4 text-[#747684] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="login-mobile"
                  inputMode="numeric"
                  autoFocus
                  autoComplete="username"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="input pl-9 !h-10 !text-[13.5px]"
                  placeholder="10-digit mobile"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684]">
                Password
              </label>
              <div className="mt-1 relative">
                <Lock className="w-4 h-4 text-[#747684] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-9 pr-10 !h-10 !text-[13.5px]"
                  placeholder="Your password"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#747684] hover:text-[#1a1b22]"
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full bg-yamaha-700 hover:bg-yamaha-800 text-white !h-10"
            >
              {submitting ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-5 text-[11px] text-[#747684] text-center">
            Lost access? Ask your administrator to reset your password.
          </div>
        </div>

        <div className="text-center text-white/40 text-[10.5px] mt-5">
          v1.0 · {new Date().getFullYear()} IVA Motors · Service Sync
        </div>
      </div>
    </div>
  );
}
